import React, { useState, useEffect, useRef } from 'react';
import {
    X, Save, Package, DollarSign, Hash, Tag, Truck, Image as ImageIcon,
    Loader2, AlertTriangle, Plus, Trash2, Upload, BarChart3
} from 'lucide-react';
import { uploadProductImage } from '../api/inventory';

const ProductModal = ({ product, categories, suppliers, onSave, onClose, isSaving }) => {
    const [formData, setFormData] = useState({
        name: '',
        sku: '',
        description: '',
        category_id: '',
        supplier_id: '',
        cost_price: '',
        selling_price: '',
        quantity: '',
        min_stock_level: '',
        unit: 'pcs',
        barcode: '',
        images: [],
        status: 'active',
    });
    const [errors, setErrors] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (product) {
            setFormData({
                name: product.name || '',
                sku: product.sku || '',
                description: product.description || '',
                category_id: product.category_id || '',
                supplier_id: product.supplier_id || '',
                cost_price: product.cost_price?.toString() || '',
                selling_price: product.selling_price?.toString() || '',
                quantity: product.quantity?.toString() || '',
                min_stock_level: product.min_stock_level?.toString() || '5',
                unit: product.unit || 'pcs',
                barcode: product.barcode || '',
                images: product.images || [],
                status: product.status || 'active',
            });
        }
    }, [product]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Product name is required';
        if (!formData.sku.trim()) newErrors.sku = 'SKU is required';
        if (!formData.selling_price || parseFloat(formData.selling_price) < 0) {
            newErrors.selling_price = 'Valid selling price is required';
        }
        if (formData.quantity && parseFloat(formData.quantity) < 0) {
            newErrors.quantity = 'Quantity cannot be negative';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const productData = {
            ...formData,
            cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
            selling_price: parseFloat(formData.selling_price),
            quantity: formData.quantity ? parseInt(formData.quantity) : 0,
            min_stock_level: formData.min_stock_level ? parseInt(formData.min_stock_level) : 5,
        };

        await onSave(productData);
    };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            const uploadPromises = files.map(file => uploadProductImage(file));
            const results = await Promise.all(uploadPromises);
            const newImages = results.map(r => r.data.url);
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...newImages],
            }));
        } catch (err) {
            console.error('Upload failed:', err);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const removeImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index),
        }));
    };

    const UNITS = ['pcs', 'kg', 'g', 'L', 'mL', 'box', 'pack', 'set', 'pair', 'dozen'];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl max-h-[90vh] flex flex-col bg-[#0d1424] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-slideUp"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#ff4d4d]/10 rounded-lg">
                            <Package className="w-5 h-5 text-[#ff4d4d]" />
                        </div>
                        <h2 className="text-xl font-bold text-white">
                            {product ? 'Edit Product' : 'Add Product'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
                            Basic Information
                        </h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Product Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    className={`w-full px-4 py-2.5 bg-white/5 border rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 transition ${
                                        errors.name ? 'border-red-500' : 'border-white/10 focus:border-[#ff4d4d]'
                                    }`}
                                    placeholder="Enter product name"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    SKU *
                                </label>
                                <div className="relative">
                                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <input
                                        type="text"
                                        value={formData.sku}
                                        onChange={(e) => handleChange('sku', e.target.value.toUpperCase())}
                                        className={`w-full pl-10 pr-4 py-2.5 bg-white/5 border rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 transition ${
                                            errors.sku ? 'border-red-500' : 'border-white/10 focus:border-[#ff4d4d]'
                                        }`}
                                        placeholder="SKU-001"
                                    />
                                </div>
                                {errors.sku && (
                                    <p className="mt-1 text-xs text-red-500">{errors.sku}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Barcode
                                </label>
                                <div className="relative">
                                    <BarChart3 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <input
                                        type="text"
                                        value={formData.barcode}
                                        onChange={(e) => handleChange('barcode', e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                                        placeholder="123456789"
                                    />
                                </div>
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    rows={3}
                                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition resize-none"
                                    placeholder="Product description..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Category & Supplier */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
                            Organization
                        </h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Category
                                </label>
                                <div className="relative">
                                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <select
                                        value={formData.category_id}
                                        onChange={(e) => handleChange('category_id', e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition appearance-none"
                                    >
                                        <option value="">No category</option>
                                        {categories?.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Supplier
                                </label>
                                <div className="relative">
                                    <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <select
                                        value={formData.supplier_id}
                                        onChange={(e) => handleChange('supplier_id', e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition appearance-none"
                                    >
                                        <option value="">No supplier</option>
                                        {suppliers?.map(sup => (
                                            <option key={sup.id} value={sup.id}>{sup.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pricing & Stock */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
                            Pricing & Stock
                        </h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Cost Price
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.cost_price}
                                        onChange={(e) => handleChange('cost_price', e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Selling Price *
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.selling_price}
                                        onChange={(e) => handleChange('selling_price', e.target.value)}
                                        className={`w-full pl-10 pr-4 py-2.5 bg-white/5 border rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 transition ${
                                            errors.selling_price ? 'border-red-500' : 'border-white/10 focus:border-[#ff4d4d]'
                                        }`}
                                        placeholder="0.00"
                                    />
                                </div>
                                {errors.selling_price && (
                                    <p className="mt-1 text-xs text-red-500">{errors.selling_price}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Current Stock
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.quantity}
                                        onChange={(e) => handleChange('quantity', e.target.value)}
                                        className={`flex-1 px-4 py-2.5 bg-white/5 border rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 transition ${
                                            errors.quantity ? 'border-red-500' : 'border-white/10 focus:border-[#ff4d4d]'
                                        }`}
                                        placeholder="0"
                                    />
                                    <select
                                        value={formData.unit}
                                        onChange={(e) => handleChange('unit', e.target.value)}
                                        className="w-24 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                                    >
                                        {UNITS.map(unit => (
                                            <option key={unit} value={unit}>{unit}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Low Stock Alert
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.min_stock_level}
                                    onChange={(e) => handleChange('min_stock_level', e.target.value)}
                                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                                    placeholder="5"
                                />
                                <p className="mt-1 text-xs text-white/40">Alert when stock falls below this level</p>
                            </div>
                        </div>
                    </div>

                    {/* Images */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
                            Product Images
                        </h3>

                        <div className="flex flex-wrap gap-3">
                            {formData.images.map((img, index) => (
                                <div key={index} className="relative group">
                                    <img
                                        src={img}
                                        alt={`Product ${index + 1}`}
                                        className="w-20 h-20 object-cover rounded-lg border border-white/10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                                className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed border-white/20 rounded-lg text-white/40 hover:text-white hover:border-white/40 transition"
                            >
                                {isUploading ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : (
                                    <>
                                        <Upload size={20} />
                                        <span className="text-xs mt-1">Upload</span>
                                    </>
                                )}
                            </button>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={handleImageUpload}
                        />
                    </div>

                    {/* Status */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
                            Status
                        </h3>
                        <div className="flex gap-3">
                            {['active', 'inactive', 'draft'].map(status => (
                                <button
                                    key={status}
                                    type="button"
                                    onClick={() => handleChange('status', status)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                        formData.status === status
                                            ? 'bg-[#ff4d4d] text-white'
                                            : 'bg-white/5 text-white/60 hover:bg-white/10'
                                    }`}
                                >
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-[#0a0f1c]">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="px-6 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition disabled:opacity-50"
                    >
                        {isSaving ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                {product ? 'Update Product' : 'Add Product'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductModal;
